import { Grid, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React from "react";
import type {
    GroupHeadingProps,
    OptionProps,
    SingleValueProps,
} from "react-select";
import {
    StatusTagChip,
    useStatusTagVisible,
    type StatusDescriptor,
} from "components/UI/StatusTags";
import { components as ReactSelectComponents } from "react-select";
import theme from "Theme/AppTheme";

export type FilterBuilderAddFilterOption = {
    title: string;
    isDisabled?: boolean;
    disabledLabel?: string;
    /** Rendered beside the title in the menu and in the collapsed value. */
    status?: StatusDescriptor;
};

export type FilterBuilderAddFilterGroup<
    TOption extends FilterBuilderAddFilterOption,
> = {
    label: string;
    options: TOption[];
};

type Props<TOption extends FilterBuilderAddFilterOption> = {
    id: string;
    options: FilterBuilderAddFilterGroup<TOption>[];
    onChange: (option?: TOption) => void;
    placeholder?: string;
    isSearchable?: boolean;
    maxMenuHeightPx?: number;
    optionValueField?: keyof TOption & string;
    selectedValue?: string;
    noValue?: boolean;
    isClearable?: boolean;
    isDisabled?: boolean;
    showStatusOnSelectedValue?: boolean;
    footerAction?: {
        id?: string;
        label: string;
        onClick: () => void;
        disabled?: boolean;
    };
};

type FilterBuilderOptionWithGroupMeta<
    TOption extends FilterBuilderAddFilterOption,
> = TOption & {
    __filterBuilderGroupLabel: string;
    __filterBuilderOptionValue: string;
};

const MENU_VIEWPORT_PADDING_PX = 16;
const MENU_FOOTER_HEIGHT_PX = 56;
const MIN_MENU_LIST_HEIGHT_PX = 120;

function FilterBuilderAddFilterSelect<
    TOption extends FilterBuilderAddFilterOption,
>({
    id,
    options,
    onChange,
    placeholder = "Filter by...",
    isSearchable = true,
    maxMenuHeightPx = 300,
    optionValueField,
    selectedValue,
    noValue = true,
    isClearable = false,
    isDisabled = false,
    showStatusOnSelectedValue = true,
    footerAction,
}: Props<TOption>) {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [collapsedGroups, setCollapsedGroups] = React.useState<
        Record<string, boolean>
    >({});
    const [menuPlacement, setMenuPlacement] = React.useState<
        "top" | "bottom"
    >("bottom");
    const menuFooterHeightPx = footerAction ? MENU_FOOTER_HEIGHT_PX : 0;
    const menuListMaxHeightPx = Math.max(
        maxMenuHeightPx - menuFooterHeightPx,
        MIN_MENU_LIST_HEIGHT_PX,
    );
    const estimatedMenuHeightPx = menuListMaxHeightPx + menuFooterHeightPx;

    const updateMenuPlacement = React.useCallback(() => {
        const anchorElement = containerRef.current;
        if (!anchorElement) {
            setMenuPlacement("bottom");
            return;
        }

        const anchorRect = anchorElement.getBoundingClientRect();
        let visibleTop = MENU_VIEWPORT_PADDING_PX;
        let visibleBottom = window.innerHeight - MENU_VIEWPORT_PADDING_PX;
        let scrollParent = anchorElement.parentElement;

        while (scrollParent) {
            const { overflowY } = window.getComputedStyle(scrollParent);
            const isScrollable = /(auto|scroll|overlay)/.test(overflowY);

            if (isScrollable && scrollParent.scrollHeight > scrollParent.clientHeight) {
                break;
            }

            scrollParent = scrollParent.parentElement;
        }

        if (scrollParent) {
            const scrollParentRect = scrollParent.getBoundingClientRect();
            visibleTop = Math.max(visibleTop, scrollParentRect.top);
            visibleBottom = Math.min(visibleBottom, scrollParentRect.bottom);
        }

        const availableSpaceAbove = anchorRect.top - visibleTop;
        const availableSpaceBelow = visibleBottom - anchorRect.bottom;

        setMenuPlacement(
            availableSpaceBelow < estimatedMenuHeightPx &&
                availableSpaceAbove > availableSpaceBelow
                ? "top"
                : "bottom",
        );
    }, [estimatedMenuHeightPx]);

    React.useEffect(() => {
        setCollapsedGroups((prev) => {
            const next: Record<string, boolean> = {};
            options.forEach((group) => {
                if (prev[group.label]) {
                    next[group.label] = true;
                }
            });
            return next;
        });
    }, [options]);

    const getOptionValue = React.useCallback(
        (option: TOption, groupLabel: string) => {
            const rawValue = optionValueField
                ? (option as Record<string, unknown>)[optionValueField]
                : undefined;

            return `${rawValue ?? `${groupLabel}:${option.title}`}`;
        },
        [optionValueField],
    );

    const optionsWithGroupMeta = React.useMemo(
        () =>
            options.map((group) => ({
                label: group.label,
                options: group.options.map(
                    (option): FilterBuilderOptionWithGroupMeta<TOption> => ({
                        ...option,
                        __filterBuilderGroupLabel: group.label,
                        __filterBuilderOptionValue: getOptionValue(
                            option,
                            group.label,
                        ),
                    }),
                ),
            })),
        [getOptionValue, options],
    );

    const selectedOption = React.useMemo(() => {
        if (noValue || !selectedValue) return undefined;

        return optionsWithGroupMeta
            .flatMap((group) => group.options)
            .find(
                (option) =>
                    option.__filterBuilderOptionValue === selectedValue,
            );
    }, [noValue, optionsWithGroupMeta, selectedValue]);

    const OptionComponent = (
        props: OptionProps<FilterBuilderOptionWithGroupMeta<TOption>, false>,
    ) => {
        const option = props.data;
        // Above the early return below — hooks must run on every render path.
        const isStatusTagVisible = useStatusTagVisible(option.status);
        const groupLabel = option.__filterBuilderGroupLabel;
        const isSearching = Boolean(
            props.selectProps.inputValue?.trim().length,
        );
        if (collapsedGroups[groupLabel] && !isSearching) return null;

        const showDisabledReason = props.isDisabled && !!option.disabledLabel;

        return (
            <ReactSelectComponents.Option {...props}>
                <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                    columnGap={1}
                >
                    <Grid item xs sx={{ minWidth: 0 }}>
                        <Grid
                            container
                            alignItems="center"
                            wrap="nowrap"
                            columnGap={1}
                        >
                            <Grid item sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: props.isDisabled
                                            ? theme.palette.gray[400]
                                            : theme.palette.gray[700],
                                        minWidth: 0,
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    {option.title}
                                </Typography>
                            </Grid>
                            {isStatusTagVisible && option.status && (
                                <Grid item>
                                    <StatusTagChip {...option.status} />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    {option.tag && (
                        <Grid item>
                            <StatusTagChip {...option.status} />
                        </Grid>
                    )}
                    {showDisabledReason && (
                        <Grid item sx={{ minWidth: 0, maxWidth: "45%" }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.gray[400],
                                    whiteSpace: "normal",
                                    textAlign: "right",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {option.disabledLabel}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </ReactSelectComponents.Option>
        );
    };

    /**
     * The selected value renders through react-select's SingleValue, not through OptionComponent,
     * so without this the tag would vanish the moment a user picks the option — and two options
     * that deliberately share a title would collapse into identical, indistinguishable UI.
     */
    const SingleValueComponent = (
        props: SingleValueProps<FilterBuilderOptionWithGroupMeta<TOption>, false>,
    ) => {
        const option = props.data;
        const isStatusTagVisible =
            useStatusTagVisible(option.status) && showStatusOnSelectedValue;

        return (
            <ReactSelectComponents.SingleValue {...props}>
                <Grid
                    container
                    alignItems="center"
                    wrap="nowrap"
                    columnGap={1}
                    sx={{ minWidth: 0 }}
                >
                    <Grid
                        item
                        sx={{
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {option.title}
                    </Grid>
                    {isStatusTagVisible && option.status && (
                        <Grid item>
                            <StatusTagChip {...option.status} />
                        </Grid>
                    )}
                </Grid>
            </ReactSelectComponents.SingleValue>
        );
    };

    const GroupHeadingComponent = (
        props: GroupHeadingProps<
            FilterBuilderOptionWithGroupMeta<TOption>,
            false
        >,
    ) => {
        const groupLabel = props.data.label;

        return (
            <div
                onClick={() =>
                    setCollapsedGroups((prev) => ({
                        ...prev,
                        [groupLabel]: !prev[groupLabel],
                    }))
                }
                style={{ cursor: "pointer" }}
            >
                <ReactSelectComponents.GroupHeading {...props} />
            </div>
        );
    };

    return (
        <div
            ref={containerRef}
            style={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
            }}
            onMouseDownCapture={updateMenuPlacement}
            onTouchStartCapture={updateMenuPlacement}
            onFocusCapture={updateMenuPlacement}
        >
            <AcxSelectSingle<FilterBuilderOptionWithGroupMeta<TOption>>
                id={id}
                options={optionsWithGroupMeta}
                valueField="__filterBuilderOptionValue"
                labelField="title"
                defaultValue={selectedOption}
                noValue={noValue}
                placeholder={placeholder}
                isSearchable={isSearchable}
                fullWidth
                minWidth="0"
                maxMenuHeightPx={menuListMaxHeightPx}
                menuPlacement={menuPlacement}
                onMenuOpen={updateMenuPlacement}
                isClearable={isClearable}
                isDisabled={isDisabled}
                components={{
                    Option: OptionComponent,
                    GroupHeading: GroupHeadingComponent,
                    SingleValue: SingleValueComponent,
                }}
                dropdownMenuEndChildren={
                    footerAction ? (
                        <Grid item sx={{ px: 1, py: 0.5 }}>
                            <AcxButton
                                id={footerAction.id}
                                onClick={footerAction.onClick}
                                disabled={footerAction.disabled}
                                sx={{
                                    justifyContent: "start",
                                    color: theme.palette.primary.main,
                                    backgroundColor: theme.palette.white.main,
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    lineHeight: "20px",
                                    px: 1,
                                    py: 0.5,
                                    width: "100%",
                                    minWidth: 0,
                                    "&:hover": {
                                        backgroundColor: theme.palette.primary[50],
                                    },
                                }}
                            >
                                {footerAction.label}
                            </AcxButton>
                        </Grid>
                    ) : undefined
                }
                onChange={(option) => {
                    if (!option) {
                        onChange(undefined);
                        return;
                    }
                    if (option.isDisabled) return;
                    onChange(option as TOption);
                }}
                customStyle={{
                    container: (provided) => ({
                        ...provided,
                        minWidth: 0,
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                    }),
                    control: (provided) => ({
                        ...provided,
                        minHeight: "36px",
                        minWidth: 0,
                        width: "100%",
                        boxSizing: "border-box",
                    }),
                    menu: (provided) => ({
                        ...provided,
                        padding: theme.spacing(1),
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        boxSizing: "border-box",
                        overflowX: "hidden",
                    }),
                }}
            />
        </div>
    );
}

export default FilterBuilderAddFilterSelect;
